import { Box, Flex } from '@televet/kibble-ui/chakra';
import { Text, Button, Divider } from '@televet/kibble-ui';
import { OttoSupportHandoffButton } from './OttoSupportHandoffButton';

/** A follow-up the clinic can ask the agent. Clicking sends `question` as a new message. */
export interface FollowUpQuestion {
  label: string;
  question: string;
}

/**
 * A call to action that opens the self-enrollment detail view for a workflow.
 *
 * Carries only the stable `Workflow.key` — never a clinic id, never display copy.
 * The consuming app resolves the key to designer-authored content and to the
 * enrollment mutation, so nothing user-visible here originates from the model.
 */
export interface WorkflowEnrollmentAction {
  label: string;
  workflowKey: string;
}

/**
 * Hands a how-do-I question to Otto support. Carries no question text: the host fills in the
 * clinic's own message, so nothing sent to support originates from the model.
 */
export interface SupportHandoffAction {
  label: string;
  supportHandoff: true;
}

export type FollowUpSuggestion = FollowUpQuestion | WorkflowEnrollmentAction | SupportHandoffAction;

export function isWorkflowEnrollmentAction(
  suggestion: FollowUpSuggestion
): suggestion is WorkflowEnrollmentAction {
  return 'workflowKey' in suggestion;
}

export function isSupportHandoffAction(
  suggestion: FollowUpSuggestion
): suggestion is SupportHandoffAction {
  return 'supportHandoff' in suggestion && suggestion.supportHandoff === true;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value !== '';

/**
 * Whether an arbitrary value is a renderable suggestion.
 *
 * Deliberately strict and field-by-field: this validates model-authored JSON that
 * arrives *partially streamed*, so an item can appear with a label before its
 * payload exists. Anything half-formed or unrecognised is rejected so the caller
 * drops it rather than rendering a button that does nothing.
 *
 * Lives beside the types it guards, and at module scope so it is not rebuilt on
 * every render of the markdown tree.
 */
export function isValidFollowUpSuggestion(value: unknown): value is FollowUpSuggestion {
  if (value === null || typeof value !== 'object') return false;

  const candidate = value as Record<string, unknown>;
  if (!isNonEmptyString(candidate.label)) return false;

  // An enrollment action carries a workflow key instead of a question.
  if ('workflowKey' in candidate) return isNonEmptyString(candidate.workflowKey);

  if ('supportHandoff' in candidate) return candidate.supportHandoff === true;

  return isNonEmptyString(candidate.question);
}

const DEFAULT_TITLE = 'Keep Exploring';
const DEFAULT_SUBTITLE = 'Pick a topic below or ask your own question';

interface OttoFollowUpSuggestionsProps {
  suggestions: FollowUpSuggestion[];
  onSuggestionClick?: (question: string) => void;
  /**
   * Opens the enrollment detail view for a workflow key. Injected rather than
   * invoked directly so this package stays presentational — it takes no tRPC or
   * router dependency, and `apps/admin` renders the same component read-only.
   */
  onWorkflowEnrollClick?: (workflowKey: string) => void;
  /**
   * Whether a workflow-enrollment suggestion should render at all. Injected for
   * the same reason as `onWorkflowEnrollClick` — this package has no eligibility
   * data of its own. Omitting it (as `apps/admin`'s read-only surfaces do) shows
   * every suggestion, matching today's behavior.
   */
  isWorkflowActionVisible?: (workflowKey: string) => boolean;
  /** Opens the host's support chat. Omitting it renders an email link in place of the chip. */
  onSupportHandoffClick?: () => void;
  disabled?: boolean;
  title?: string;
  subtitle?: string;
}

function toTestId(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '-').slice(0, 40);
}

/**
 * Drops a workflow-enrollment suggestion the caller says isn't visible right
 * now. Plain questions always pass through — only `isWorkflowActionVisible`
 * decides an enrollment action's fate, and no predicate means show everything.
 */
export function filterVisibleSuggestions(
  suggestions: FollowUpSuggestion[],
  isWorkflowActionVisible?: (workflowKey: string) => boolean
): FollowUpSuggestion[] {
  return suggestions.filter(
    (suggestion) =>
      !isWorkflowEnrollmentAction(suggestion) ||
      !isWorkflowActionVisible ||
      isWorkflowActionVisible(suggestion.workflowKey)
  );
}

export function OttoFollowUpSuggestions({
  suggestions,
  onSuggestionClick,
  onWorkflowEnrollClick,
  isWorkflowActionVisible,
  onSupportHandoffClick,
  disabled,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
}: OttoFollowUpSuggestionsProps) {
  const visibleSuggestions = filterVisibleSuggestions(suggestions, isWorkflowActionVisible);
  if (visibleSuggestions.length === 0) return null;
  // "Keep Exploring" reads wrong above a lone support chip, which is a handoff, not a topic.
  const isSupportHandoffOnly = visibleSuggestions.every(isSupportHandoffAction);

  return (
    <Flex direction="column" gap={2} mt={2}>
      <Divider mt={2} />
      {!isSupportHandoffOnly && (
        <Box>
          <Text fontSize="lg" fontWeight="semibold" variant="default">
            {title}
          </Text>
          <Text fontSize="xs" variant="subtle">
            {subtitle}
          </Text>
        </Box>
      )}
      <Flex wrap="wrap" gap={2}>
        {visibleSuggestions.map((suggestion) =>
          isSupportHandoffAction(suggestion) ? (
            <OttoSupportHandoffButton
              key={suggestion.label}
              onSupportHandoffClick={onSupportHandoffClick}
              disabled={disabled}
            />
          ) : isWorkflowEnrollmentAction(suggestion) ? (
            <Button
              // Keyed on label rather than the payload: an enrollment action has no
              // `question`, and label is what distinguishes buttons within a block.
              key={suggestion.label}
              data-testid={`otto-pilot-workflow-enroll-${toTestId(suggestion.label)}`}
              variant="primary"
              size="sm"
              iconName="workflows"
              disabled={disabled || !onWorkflowEnrollClick}
              onClick={() => onWorkflowEnrollClick?.(suggestion.workflowKey)}
            >
              {suggestion.label}
            </Button>
          ) : (
            <Button
              key={suggestion.label}
              data-testid={`otto-pilot-suggestion-${toTestId(suggestion.label)}`}
              variant="secondaryGradientFull"
              size="sm"
              disabled={disabled || !onSuggestionClick}
              onClick={() => onSuggestionClick?.(suggestion.question)}
            >
              {suggestion.label}
            </Button>
          )
        )}
      </Flex>
    </Flex>
  );
}
